export type GraphLevel = 'vault' | 'model' | 'asset';
export type OwnershipLevel = 'VAULT' | 'MODEL' | 'ASSET';
export type GraphPolicyType = 'ROW_LEVEL' | 'COLUMN_LEVEL' | 'ACCESS_CONTROL' | 'TIME_BASED' | 'SENSITIVE_DATA';
export type GraphPolicyStatus = 'ACTIVE' | 'DRAFT';
export type GraphClassification = 'Public' | 'Internal' | 'Confidential' | 'Restricted' | 'PII' | 'PHI' | 'PCI';
export type GraphMaskKind = 'partial' | 'full';
export type GraphActionKind = 'allow' | 'deny' | 'mask' | 'filter';
export type OverlapKind = 'compatible' | 'conflict' | 'unresolved';
export type EffectiveKind = 'allow' | 'deny' | 'mask' | 'filter' | 'unresolved';
export type GraphRelation = 'owned_by' | 'inherited_by' | 'protects' | 'overlaps_with' | 'depends_on';
export type GraphNodeKind = 'vault' | 'model' | 'policy' | 'asset' | 'column' | 'overflow';

export const POLICY_TYPE_LABEL: Record<GraphPolicyType, string> = {
  ROW_LEVEL: 'Row-Level',
  COLUMN_LEVEL: 'Column-Level',
  ACCESS_CONTROL: 'Access Control',
  TIME_BASED: 'Time-Based',
  SENSITIVE_DATA: 'Sensitive Data',
};

export const OWNERSHIP_LABEL: Record<OwnershipLevel, string> = {
  VAULT: 'DataVault',
  MODEL: 'Data Model',
  ASSET: 'Asset',
};

export const RELATION_LABEL: Record<GraphRelation, string> = {
  owned_by: 'Owned by',
  inherited_by: 'Inherited by',
  protects: 'Protects',
  overlaps_with: 'Overlaps with',
  depends_on: 'Depends on',
};

export interface GraphColumn {
  name: string;
  type: string;
  classification: GraphClassification;
}

export interface GraphAsset {
  id: string;
  name: string;
  vaultId: string;
  modelIds: string[];
  source: string;
  featured: boolean;
  columns: GraphColumn[];
}

export interface GraphModel {
  id: string;
  name: string;
  vaultId: string;
  assetIds: string[];
}

export interface GraphVault {
  id: string;
  name: string;
  description: string;
}

export interface GraphSubject {
  mode: 'everyone' | 'roles';
  roles?: string[];
  exemptRoles?: string[];
  label: string;
}

export interface GraphPolicyAction {
  kind: GraphActionKind;
  mask?: GraphMaskKind;
  denyTarget?: 'asset' | 'column';
  filter?: string;
  label: string;
}

export interface GraphPolicyMatch {
  classifications?: GraphClassification[];
  columnNames?: string[];
  assetIds?: string[];
  requireRegion?: boolean;
  requireConsent?: boolean;
}

export interface GraphPolicy {
  id: string;
  name: string;
  description: string;
  type: GraphPolicyType;
  status: GraphPolicyStatus;
  ownership: {
    level: OwnershipLevel;
    vaultId: string;
    modelId?: string;
    assetId?: string;
  };
  mandatory: boolean;
  audience: GraphSubject;
  targeting: string;
  action: GraphPolicyAction;
  match: GraphPolicyMatch;
  dependsOnAssetId?: string;
}

export interface GraphUserContext {
  id: string;
  label: string;
  roles: string[];
  region: string;
}

export interface GraphCatalog {
  vaults: GraphVault[];
  models: GraphModel[];
  assets: GraphAsset[];
  policies: GraphPolicy[];
  users: GraphUserContext[];
}

export interface PolicyCoverage {
  policyId: string;
  assetsEvaluated: number;
  assetsMatched: number;
  columnsProtected: number;
  matchedAssetIds: string[];
  scopedAssetIds: string[];
  protectedColumns: Array<{ assetId: string; column: string }>;
}

export interface ApplicablePolicyHit {
  policy: GraphPolicy;
  origin: OwnershipLevel;
  actionLabel: string;
  appliesToUser: boolean;
}

export interface EffectiveRestriction {
  kind: EffectiveKind;
  label: string;
  explanation: string;
  attention: boolean;
  hits: ApplicablePolicyHit[];
  filters: string[];
  unresolved: string[];
  simulation: true;
}

export interface PolicyOverlap {
  id: string;
  policyIds: [string, string];
  resource: { kind: 'asset' | 'column'; assetId: string; column?: string; label: string };
  kind: OverlapKind;
  effectiveLabel: string;
  explanation: string;
  attention: boolean;
}

export interface GraphContext {
  level: GraphLevel;
  vaultId: string;
  modelId?: string;
  assetId?: string;
}

export interface GraphNodeData {
  kind: GraphNodeKind;
  title: string;
  subtitle: string;
  meta: string;
  expandable: boolean;
  expanded: boolean;
  overflowCount?: number;
  origin?: 'inherited' | 'local';
}

export const EXPANSION_CAP = 8;
export const GRAPH_NODE_W = 240;
export const GRAPH_NODE_H = 88;
export const GRAPH_NODE_GAP_X = 48;
export const GRAPH_LAYER_Y = [32, 192, 352, 512, 672] as const;
